import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Tableau } from '~commonInfra/utils/TableauLog';
import { isAdminInsightsContent } from '~src/adminInsights/services/AdminInsightsIdentificationService';
import { IDataSourceDetailInfo } from '~src/datasource/models/IDataSourceInfo';
import { FlowDetailInfo } from '~src/flow/models/FlowInfo';
import { IWorkbookDetailInfo } from '~src/workbook/models/IWorkbookInfo';

import {
  AdminInsightsAction,
  sendAdminInsightsDatasourceTelemetry,
  sendAdminInsightsFlowTelemetry,
  sendAdminInsightsWorkbookTelemetry,
} from './AdminInsightsPftTelemetry';

/**
 * Checks if the given workbook belongs to the Admin Insights project and,
 * if so, emits the `admin_insights_workbook` PFT event.
 *
 * This function is fire-and-forget (async but not awaited by the caller).
 * Failures are swallowed to avoid disrupting the workbook load UX.
 */
export async function emitAdminInsightsWorkbookEvent(
  workbookDetail: IWorkbookDetailInfo,
  action: AdminInsightsAction = 'view',
): Promise<void> {
  try {
    if (!FeatureFlags.adminInsightsPftTelemetryEnabled) {
      return;
    }

    const projectId = workbookDetail.project?.id;
    if (!projectId) {
      return;
    }

    const isAdminInsights = await isAdminInsightsContent(projectId);
    if (!isAdminInsights) {
      return;
    }

    sendAdminInsightsWorkbookTelemetry({
      projectId,
      contentLuid: workbookDetail.luid ?? '',
      contentName: workbookDetail.name ?? '',
      action,
    });
  } catch (e) {
    // Telemetry must never disrupt the primary user flow
    Tableau.Logger().warn('Admin Insights workbook telemetry failed: ' + e);
  }
}

/**
 * Checks if the given datasource belongs to the Admin Insights project and,
 * if so, emits the `admin_insights_datasource` PFT event.
 *
 * Same fire-and-forget pattern as the workbook emitter.
 */
export async function emitAdminInsightsDatasourceEvent(
  datasourceDetail: IDataSourceDetailInfo,
  action: AdminInsightsAction = 'view',
): Promise<void> {
  try {
    if (!FeatureFlags.adminInsightsPftTelemetryEnabled) {
      return;
    }

    const projectId = datasourceDetail.project?.id;
    if (!projectId) {
      return;
    }

    const isAdminInsights = await isAdminInsightsContent(projectId);
    if (!isAdminInsights) {
      return;
    }

    sendAdminInsightsDatasourceTelemetry({
      projectId,
      contentLuid: datasourceDetail.luid ?? '',
      contentName: datasourceDetail.name ?? '',
      action,
    });
  } catch (e) {
    // Telemetry must never disrupt the primary user flow
    Tableau.Logger().warn('Admin Insights datasource telemetry failed: ' + e);
  }
}

/**
 * Checks if the given flow belongs to the Admin Insights project and,
 * if so, emits the `admin_insights_flow` PFT event.
 *
 * Same fire-and-forget pattern as the workbook/datasource emitters. Fired
 * when a Prep flow in the Admin Insights project is published.
 */
export async function emitAdminInsightsFlowEvent(
  flowDetail: FlowDetailInfo,
  action: AdminInsightsAction = 'publish',
): Promise<void> {
  try {
    if (!FeatureFlags.adminInsightsPftTelemetryEnabled) {
      return;
    }

    const projectId = flowDetail.project?.id;
    if (!projectId) {
      return;
    }

    const isAdminInsights = await isAdminInsightsContent(projectId);
    if (!isAdminInsights) {
      return;
    }

    sendAdminInsightsFlowTelemetry({
      projectId,
      contentLuid: flowDetail.luid ?? '',
      contentName: flowDetail.name ?? '',
      action,
    });
  } catch (e) {
    // Telemetry must never disrupt the primary user flow
    Tableau.Logger().warn('Admin Insights flow telemetry failed: ' + e);
  }
}

/**
 * Content-type-agnostic emitter for callers that only have the structural
 * identifiers (project id, LUID, name) rather than a full detail object --
 * e.g. bulk content operations that act on `IContentInfo`-shaped items.
 *
 * Same fire-and-forget pattern as the detail emitters: checks Admin Insights
 * membership and dispatches to the workbook or datasource send helper based
 * on `contentType`.
 */
export async function emitAdminInsightsContentEvent(
  contentType: 'workbook' | 'datasource',
  content: { projectId?: string, luid?: string, name?: string },
  action: AdminInsightsAction,
): Promise<void> {
  try {
    if (!FeatureFlags.adminInsightsPftTelemetryEnabled) {
      return;
    }

    if (!content.projectId) {
      return;
    }

    const isAdminInsights = await isAdminInsightsContent(content.projectId);
    if (!isAdminInsights) {
      return;
    }

    const params = {
      projectId: content.projectId,
      contentLuid: content.luid ?? '',
      contentName: content.name ?? '',
      action,
    };

    if (contentType === 'workbook') {
      sendAdminInsightsWorkbookTelemetry(params);
    } else {
      sendAdminInsightsDatasourceTelemetry(params);
    }
  } catch (e) {
    // Telemetry must never disrupt the primary user flow
    Tableau.Logger().warn('Admin Insights content telemetry failed: ' + e);
  }
}
